import { showErrorNotification } from 'core/utils/notification-service';
import React, { useEffect, useState } from 'react';
import { CERTIFICATE_FILE_TYPES, getFileExtension } from './settings-devices-eap-settings-importing.business';

export type UploadStatus = 'idle' | 'uploading' | 'uploaded';

export type UploadFileItem = {
    id: string;
    name: string;
    status: UploadStatus;
    progress: number;
    file: File;
    content?: string;
};

export type MissingCertificateUploadStatus = 'idle' | 'uploading' | 'uploaded';

type UseEapImportUploadParams = {
    missingCertificates: string[];
    t: (key: string, params?: Record<string, unknown>) => string;
    setMissingCertificateUploadStatus: React.Dispatch<React.SetStateAction<MissingCertificateUploadStatus>>;
};

export const useEapImportUpload = ({
    missingCertificates,
    t,
    setMissingCertificateUploadStatus,
}: UseEapImportUploadParams) => {
    const [files, setFiles] = useState<UploadFileItem[]>([]);

    useEffect(() => {
        setMissingCertificateUploadStatus((previousStatus) => {
            if (files.length === 0) {
                return previousStatus === 'idle' ? previousStatus : 'idle';
            }

            const nextStatus: MissingCertificateUploadStatus = files.some((file) => file.status === 'uploading')
                ? 'uploading'
                : 'uploaded';

            return previousStatus === nextStatus ? previousStatus : nextStatus;
        });
    }, [files, setMissingCertificateUploadStatus]);

    const updateFileById = (fileId: string, updater: (file: UploadFileItem) => UploadFileItem) => {
        setFiles((prev) => prev.map((file) => (file.id === fileId ? updater(file) : file)));
    };

    const setFileProgress = (fileId: string, progress: number) => {
        updateFileById(fileId, (file) => ({ ...file, progress }));
    };

    const setFileUploaded = (fileId: string, content: string) => {
        updateFileById(fileId, (file) => ({
            ...file,
            status: 'uploaded',
            progress: 100,
            content,
        }));
    };

    const setFileUploadError = (fileId: string) => {
        showErrorNotification(
            t('settings.devices.eap-settings.eap-config-list.action.import.modal.notification.upload-error')
        );
        updateFileById(fileId, (file) => ({ ...file, status: 'idle', progress: 0 }));
    };

    const uploadSinglePemFile = (fileItem: UploadFileItem, originalFile: File) => {
        const { id: fileId } = fileItem;

        const reader = new FileReader(); // NOSONAR - FileReader needed for progress tracking

        reader.onprogress = (event) => {
            if (event.lengthComputable) {
                const percent = Math.round((event.loaded / event.total) * 100);
                setFileProgress(fileId, percent);
            }
        };

        reader.onload = () => {
            setFileUploaded(fileId, reader.result as string);
        };

        reader.onerror = () => {
            setFileUploadError(fileId);
        };

        reader.readAsText(originalFile); // NOSONAR - Blob#text() has no progress events
    };

    const onLoadFile = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const fileList = ev.target.files;
        if (!fileList?.length) {
            return;
        }

        const allowedNames = new Set(missingCertificates);
        const alreadyUploadedNames = new Set(files.map((f) => f.name));
        const validFiles: File[] = [];
        const invalidFiles: string[] = [];
        const duplicateFiles: string[] = [];

        for (const file of Array.from(fileList)) {
            if (!allowedNames.has(file.name)) {
                invalidFiles.push(file.name);
            } else if (alreadyUploadedNames.has(file.name)) {
                duplicateFiles.push(file.name);
            } else {
                validFiles.push(file);
            }
        }

        if (invalidFiles.length > 0) {
            showErrorNotification(
                t(
                    'settings.devices.eap-settings.eap-config-list.action.import.importing.certificates.upload.invalid-filename',
                    {
                        filenames: invalidFiles.join(', '),
                    }
                )
            );
        }

        if (duplicateFiles.length > 0) {
            showErrorNotification(
                t(
                    'settings.devices.eap-settings.eap-config-list.action.import.importing.certificates.upload.duplicate-filename',
                    {
                        filenames: duplicateFiles.join(', '),
                    }
                )
            );
        }

        if (!validFiles.length) {
            return;
        }

        const newFiles: UploadFileItem[] = validFiles.map((file) => {
            const fileExtension = getFileExtension(file.name);

            return {
                id: `${file.name}-${Date.now()}`,
                name: file.name,
                status: CERTIFICATE_FILE_TYPES.includes(fileExtension) ? 'uploading' : 'uploaded',
                progress: 0,
                file,
            };
        });

        setFiles((prev) => [...prev, ...newFiles]);

        for (const [index, fileItem] of newFiles.entries()) {
            const originalFile = validFiles[index];
            const fileExtension = getFileExtension(originalFile.name);

            if (!CERTIFICATE_FILE_TYPES.includes(fileExtension)) {
                continue;
            }

            uploadSinglePemFile(fileItem, originalFile);
        }
    };

    const onDismissFile = (fileId: string) => {
        setFiles((prev) => prev.filter((f) => f.id !== fileId));
    };

    const resetFiles = () => {
        setFiles([]);
    };

    return {
        files,
        onLoadFile,
        onDismissFile,
        resetFiles,
    };
};
